// src/components/ProcessViewer.tsx - Korrigerad för att ta bort dubblerad kategori-del
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import ProcessNavigation from "@/components/ProcessNavigation";
import MomentList from "@/components/MomentList";
import { useProcessData } from "@/hooks/useProcessData";
import { 
  parseDocumentLinks, 
  parseParticipants, 
  parseParticipantsWithResponsibilities,
  getUniqueParticipants,
  hasAlternativeData,
  getParticipantsForAlternative 
} from "@/utils/processUtils";
import { useToast } from "@/hooks/use-toast";
import useSessionState from "@/hooks/useSessionState";
import { ProcessItem, EnhancedDocumentLink, FilterState } from "@/types";
import DocumentListModal from "@/components/DocumentListModal";
import EnhancedTutorialGuide from "@/components/TutorialGuide";
import QuizModal from "@/components/EnhancedQuizModal"; 
import ImprovedMomentDetailLayout from "@/components/ImprovedMomentDetailLayout"; 
import ImprovedDocumentsColumn from "@/components/ImprovedDocumentsColumn";
import { OptimizedStarryBackground } from "./OptimizedStarryBackground";
import { ChevronLeft, LogOut, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProcessViewerProps {
  accessToken: string;
  businessArea?: string | null;
  category?: string | null;
  onBackToAreas?: () => void;
  onBackToCategories?: () => void;
}

const ProcessViewer: React.FC<ProcessViewerProps> = ({ 
  accessToken, 
  businessArea = null,
  category = null,
  onBackToAreas,
  onBackToCategories
}) => {
  // Log received props for debugging
  console.log("ProcessViewer rendered with props:", { businessArea, category });

  // Fetch data with our hook, now with businessArea and category parameters
  const { data: rawData, loading, error, refetch } = useProcessData(accessToken, businessArea, category);
  const { toast } = useToast();

  // State for active selections and search with session storage
  const [activeMainProcess, setActiveMainProcess] = useSessionState<string>(
    "activeMainProcess",
    ""
  );
  const [activeSubProcess, setActiveSubProcess] = useSessionState<string>(
    "activeSubProcess",
    ""
  );
  const [activeMoment, setActiveMoment] = useSessionState<string>(
    "activeMoment",
    ""
  );
  const [searchTerm, setSearchTerm] = useSessionState<string>("searchTerm", "");
  const [searchResults, setSearchResults] = useSessionState<ProcessItem[]>(
    "searchResults",
    []
  );
  
  // För att hålla koll på om vi har gjort en första initialisering
  const [initializedSession, setInitializedSession] = useState(false);
  const [initializationAttempted, setInitializationAttempted] = useState(false);

  // State for filter handling
  const [filters, setFilters] = useState<FilterState>({
    roles: [],
    documentTypes: [],
    showOnlyMyTasks: false,
    currentUserRole: undefined,
    showImportantOnly: false,
    showWithDocumentsOnly: false
  });

  // State for document list modal
  const [isDocumentListOpen, setIsDocumentListOpen] = useState(false);
  const [documentList, setDocumentList] = useState<EnhancedDocumentLink[]>([]);

  // State for tutorial guide
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  
  // State for quiz modal
  const [isQuizOpen, setIsQuizOpen] = useState(false);

  // Debugging for black screen issue
  useEffect(() => {
    console.log("ProcessViewer data status:", { 
      businessArea, 
      category, 
      dataLength: rawData.length,
      loading,
      error,
      activeMainProcess,
      activeSubProcess,
      activeMoment,
      initializationAttempted
    });
  }, [businessArea, category, rawData.length, loading, error, activeMainProcess, activeSubProcess, activeMoment, initializationAttempted]);

  // Set initial active process when data is available
  useEffect(() => {
    if (rawData.length > 0 && !initializedSession) {
      console.log("Initializing session with data", { dataLength: rawData.length });
      
      // Sätt flaggan för att undvika upprepad initialisering
      setInitializedSession(true);
      
      // Check if we have saved selections in the session
      const hasSession = activeMainProcess && activeSubProcess && activeMoment;

      // If we don't have saved selections, use the first object
      if (!hasSession) {
        console.log("No session found, using first item for navigation");
        const firstItem = rawData[0];
        setActiveMainProcess(firstItem.Title);
        setActiveSubProcess(firstItem.field_0);
        setActiveMoment(firstItem.field_2);
        return;
      }
      
      // Om vi har sparade val, verifiera att de finns i den nya datan
      const mainExists = rawData.some(
        (item) => item.Title === activeMainProcess
      );
      const subExists = rawData.some(
        (item) =>
          item.Title === activeMainProcess &&
          item.field_0 === activeSubProcess
      );
      const momentExists = rawData.some(
        (item) =>
          item.Title === activeMainProcess &&
          item.field_0 === activeSubProcess &&
          item.field_2 === activeMoment
      );

      // Om någon del av den sparade sökvägen är ogiltig,
      // återställ till det första objektet
      if (!mainExists || !subExists || !momentExists) {
        console.log("Saved session invalid, resetting to first item");
        const firstItem = rawData[0];
        setActiveMainProcess(firstItem.Title);
        setActiveSubProcess(firstItem.field_0);
        setActiveMoment(firstItem.field_2);

        toast({
          title: "Sessionen återställd",
          description: "Den sparade positionen kunde inte återställas.",
          duration: 3000,
        });
      } else {
        console.log("Using saved session for navigation");
        // Informera användaren om att sessionen har återställts, 
        // bara om vi verkligen har återställt något
        toast({
          title: "Session återställd",
          description: "Din tidigare position har återställts.",
          duration: 3000,
        });
      }
    } else if (rawData.length === 0 && !initializationAttempted && !loading) {
      // If we have no data and haven't attempted initialization yet,
      // mark that we attempted it to avoid infinite loops
      console.log("No data found, marking initialization as attempted");
      setInitializationAttempted(true);
    }
  }, [rawData, activeMainProcess, activeSubProcess, activeMoment, initializedSession, initializationAttempted, loading, setActiveMainProcess, setActiveSubProcess, setActiveMoment, toast]);

  // Calculate main process list
  const mainProcesses = useMemo(() => {
    const uniqueTitles = [...new Set(rawData.map((item) => item.Title))];
    console.log(`Found ${uniqueTitles.length} unique main processes`);
    return uniqueTitles
      .map((title) => {
        const items = rawData.filter((item) => item.Title === title);
        const firstItem = items[0];
        return {
          title,
          number: firstItem?.hierarchicalNumber?.split(".")[0] || "0",
        };
      })
      .sort((a, b) => Number(a.number) - Number(b.number));
  }, [rawData]);

  // Calculate subprocess list based on active main process
  const subProcesses = useMemo(() => {
    const filtered = rawData.filter((item) => item.Title === activeMainProcess);
    const uniqueSubs = [...new Set(filtered.map((item) => item.field_0))];
    console.log(`Found ${uniqueSubs.length} unique subprocesses for ${activeMainProcess}`);
    return uniqueSubs
      .map((sub) => {
        const items = filtered.filter((item) => item.field_0 === sub);
        const firstItem = items[0];
        if (firstItem?.hierarchicalNumber) {
          const [m, s] = firstItem.hierarchicalNumber.split(".");
          return { title: sub, number: `${m}.${s}` };
        }
        return { title: sub, number: "0.0" };
      })
      .sort((a, b) => {
        const [, aNum] = a.number.split(".");
        const [, bNum] = b.number.split(".");
        return Number(aNum || 0) - Number(bNum || 0);
      });
  }, [rawData, activeMainProcess]);

  // Filter processData based on active filters
  const filteredProcessData = useMemo(() => {
    let filtered = [...rawData];

    // Filter by roles if any are selected
    if (filters.roles.length > 0) {
      filtered = filtered.filter((item) => {
        // Check only the Deltagareochansvar fields
        const alt1Participants = item.Deltagareochansvar1 
          ? parseParticipantsWithResponsibilities(item.Deltagareochansvar1) 
          : [];
          
        const alt2Participants = item.Deltagareochansvar2 
          ? parseParticipantsWithResponsibilities(item.Deltagareochansvar2) 
          : [];
        
        // Combine all roles from both alternatives
        const allRoles = [...alt1Participants, ...alt2Participants].map(p => p.role);
        
        // Check if any of the selected roles match
        return filters.roles.some(role => allRoles.includes(role));
      });
    }

    // Filter by "Important" flag if enabled
    if (filters.showImportantOnly) {
      filtered = filtered.filter((item) => 
        Boolean(item.Viktigt) && item.Viktigt?.trim() !== ""
      );
    }

    // Filter by document presence if enabled
    if (filters.showWithDocumentsOnly) {
      filtered = filtered.filter((item) => 
        Boolean(item.field_4) && item.field_4.trim() !== ""
      );
    }

    return filtered;
  }, [rawData, filters]);

  // Collect main processes, subprocesses, and moments that contain filtered roles
  // for visual indication
  const highlightMap = useMemo(() => {
    const map = {
      mainProcesses: new Set<string>(),
      subProcesses: new Map<string, Set<string>>(),
      moments: new Map<string, Map<string, Set<string>>>(),
    };

    // Check if we have any active filters
    const hasActiveFilters = filters.roles.length > 0 || 
                             filters.showImportantOnly || 
                             filters.showWithDocumentsOnly;

    // If we have active filters, mark processes with matching items
    if (hasActiveFilters && rawData.length > 0) {
      // Find all items matching our filters
      const matchingItems = rawData.filter(item => {
        let matchesRoles = true;
        let matchesImportant = true;
        let matchesDocuments = true;
        
        // Check role filters if any are selected
        if (filters.roles.length > 0) {
          const alt1Participants = item.Deltagareochansvar1 
            ? parseParticipantsWithResponsibilities(item.Deltagareochansvar1) 
            : [];
            
          const alt2Participants = item.Deltagareochansvar2 
            ? parseParticipantsWithResponsibilities(item.Deltagareochansvar2) 
            : [];
          
          const allRoles = [...alt1Participants, ...alt2Participants].map(p => p.role);
          
          matchesRoles = filters.roles.some(role => allRoles.includes(role));
        }
        
        // Check Important filter if enabled
        if (filters.showImportantOnly) {
          matchesImportant = Boolean(item.Viktigt) && item.Viktigt?.trim() !== "";
        }
        
        // Check documents filter if enabled
        if (filters.showWithDocumentsOnly) {
          matchesDocuments = Boolean(item.field_4) && item.field_4.trim() !== "";
        }
        
        return matchesRoles && matchesImportant && matchesDocuments;
      });

      // Add all matching items to highlight map
      matchingItems.forEach((item) => {
        // Mark main process
        map.mainProcesses.add(item.Title);

        // Initialize subprocesses for this main process if needed
        if (!map.subProcesses.has(item.Title)) {
          map.subProcesses.set(item.Title, new Set<string>());
        }
        map.subProcesses.get(item.Title)?.add(item.field_0);

        // Initialize moments for this main+subprocess if needed
        if (!map.moments.has(item.Title)) {
          map.moments.set(item.Title, new Map<string, Set<string>>());
        }
        if (!map.moments.get(item.Title)?.has(item.field_0)) {
          map.moments.get(item.Title)?.set(item.field_0, new Set<string>());
        }
        map.moments.get(item.Title)?.get(item.field_0)?.add(item.field_2);
      });
    }

    return map;
  }, [rawData, filters]);

  // Calculate moments based on active main and subprocess with filter handling
  const moments = useMemo(() => {
    const filteredForMoments = filteredProcessData
      .filter(
        (item) =>
          item.Title === activeMainProcess && item.field_0 === activeSubProcess
      );
      
    console.log(`Found ${filteredForMoments.length} moments for ${activeMainProcess} > ${activeSubProcess}`);
      
    return filteredForMoments
      .map((item) => ({
        title: item.field_2,
        description: item.field_3,
        hierarchicalNumber: item.hierarchicalNumber,
        documents: parseDocumentLinks(item.field_4),
        participants: parseParticipants(item.field_5),
        viktigt: item.Viktigt || "",
        detailedDescription: item.Detaljeradbeskrivning
      }))
      .sort((a, b) => {
        const [, , aNum] = (a.hierarchicalNumber || "").split(".");
        const [, , bNum] = (b.hierarchicalNumber || "").split(".");
        return Number(aNum || 0) - Number(bNum || 0);
      });
  }, [filteredProcessData, activeMainProcess, activeSubProcess]);

  // Determine active process detail for rendering
  const activeProcessData = useMemo(() => {
    return moments.find((m) => m.title === activeMoment);
  }, [moments, activeMoment]);

  // Find original process item for active moment (for participant handling)
  const originalProcessItem = useMemo(() => {
    return rawData.find(item => 
      item.Title === activeMainProcess && 
      item.field_0 === activeSubProcess && 
      item.field_2 === activeMoment
    );
  }, [rawData, activeMainProcess, activeSubProcess, activeMoment]);

  // Search functionality
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }
  
    const termLower = term.toLowerCase();
  
    // Improved search logic with weighting
    const searchInField = (field: string | undefined, weight: number) => {
      return field && field.toLowerCase().includes(termLower) ? weight : 0;
    };
  
    // Search through all fields and weight the matches
    const results = rawData
      .map((item) => {
        // Calculate relevance score for each object
        const titleScore = searchInField(item.Title, 10);
        const subProcessScore = searchInField(item.field_0, 8);
        const momentScore = searchInField(item.field_2, 8);
        const descriptionScore = searchInField(item.field_3, 5);
        const documentScore = searchInField(item.field_4, 3);
        const categoryScore = searchInField(item.Kategori, 5);
        
        // Search in the Deltagareochansvar fields
        const participantsScore1 = searchInField(item.Deltagareochansvar1, 3);
        const participantsScore2 = searchInField(item.Deltagareochansvar2, 3);
  
        // Total score for the object
        const totalScore =
          titleScore +
          subProcessScore +
          momentScore +
          descriptionScore +
          documentScore +
          categoryScore +
          participantsScore1 +
          participantsScore2;
  
        return {
          item,
          score: totalScore,
        };
      })
      .filter((result) => result.score > 0) // Filter out objects without matches
      .sort((a, b) => b.score - a.score) // Sort by relevance
      .map((result) => result.item); // Map back to just the object
  
    setSearchResults(results);
  };

  // Navigate to a search result, with the ability to focus on a certain part
  const navigateToResult = (
    item: ProcessItem,
    focusType?: "document" | "participant" | "description"
  ) => {
    setActiveMainProcess(item.Title);
    setActiveSubProcess(item.field_0);
    setActiveMoment(item.field_2);
    setSearchTerm("");
    setSearchResults([]);

    // Show toast with feedback
    toast({
      title: "Navigerat till",
      description: `${item.Title} → ${item.field_0} → ${item.field_2}`,
      duration: 2000,
    });

    // Scroll to relevant section if a focus is specified
    if (focusType) {
      setTimeout(() => {
        const sectionMap = {
          description: document.querySelector(".scroll-to-description"),
          document: document.querySelector(".scroll-to-documents"),
          participant: document.querySelector(".scroll-to-participants"),
        };

        const element = sectionMap[focusType];
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  // Show document list
  const handleShowDocumentList = (documents: EnhancedDocumentLink[]) => {
    setDocumentList(documents);
    setIsDocumentListOpen(true);
  };
  
  // Show quiz modal
  const handleShowQuiz = () => {
    setIsQuizOpen(true);
  };

  // Close search box when clicking outside
  const searchContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setSearchTerm("");
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Manual refetch if no data
  useEffect(() => {
    if (rawData.length === 0 && !loading && initializationAttempted) {
      // If we still have no data after our first initialization attempt,
      // try to refetch once
      console.log("No data after initialization attempt, trying to refetch");
      refetch();
    }
  }, [rawData.length, loading, initializationAttempted, refetch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-6 flex items-center justify-center" id="loading-container">
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <OptimizedStarryBackground />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-6"
        >
          <div className="relative w-16 h-16" id="loading-spinner">
            <motion.div
              className="absolute inset-0 rounded-full border-t-2 border-orange-300"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-2 rounded-full border-t-2 border-blue-300"
              animate={{ rotate: -180 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <div className="text-center">
            <p className="text-lg mb-2" id="loading-text">Laddar processer...</p>
            <p className="text-sm text-gray-400" id="loading-subtext">Hämtar data från SharePoint</p>
            {category && (
              <p className="text-sm text-orange-300 mt-2">Kategori: {category}</p>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-950 text-red-400 p-6" id="error-container">
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <OptimizedStarryBackground />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-red-900/20 border border-red-500/30 p-6 rounded-lg max-w-2xl mx-auto" id="error-message">
            <h2 className="text-xl font-medium mb-4 text-red-300" id="error-title">
              Anslutningsfel
            </h2>
            <p className="mb-4" id="error-subtitle">Ett fel uppstod vid hämtning av data:</p>
            <div className="bg-red-950/30 p-4 rounded-md text-red-200 font-mono text-sm overflow-auto" id="error-details">
              {error}
            </div>
            <div className="mt-6 flex justify-between">
              <button 
                onClick={() => refetch()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Försök igen
              </button>
              <Button
                onClick={onBackToCategories || onBackToAreas || (() => window.location.reload())}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                {onBackToCategories ? "Tillbaka till kategorier" : onBackToAreas ? "Tillbaka till affärsområden" : "Ladda om sidan"}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show a message if no process data is found
  if (rawData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-950 text-white p-6 flex items-center justify-center" id="no-data-container">
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <OptimizedStarryBackground />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-800/70 p-6 rounded-xl border border-gray-700 max-w-lg mx-auto text-center"
        >
          <h2 className="text-xl font-medium mb-4">Inga processer hittades</h2>
          <p className="mb-6 text-gray-300">
            {category
              ? `Inga processer hittades för ${businessArea} inom kategorin "${category}".`
              : `Inga processer hittades för ${businessArea}.`
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {onBackToCategories && (
              <Button
                onClick={onBackToCategories}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <ChevronLeft className="mr-2 h-5 w-5" />
                Tillbaka till kategorier
              </Button>
            )}
            {onBackToAreas && (
              <Button
                onClick={onBackToAreas}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                <ChevronLeft className="mr-2 h-5 w-5" />
                Tillbaka till affärsområden
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // If we have data but don't have an active main process yet (perhaps it was reset),
  // Initialize it with the first item
  if (rawData.length > 0 && (!activeMainProcess || !activeSubProcess || !activeMoment)) {
    const firstItem = rawData[0];
    console.log("Setting initial navigation from available data");
    if (!activeMainProcess) setActiveMainProcess(firstItem.Title);
    if (!activeSubProcess) setActiveSubProcess(firstItem.field_0);
    if (!activeMoment) setActiveMoment(firstItem.field_2);
    
    // Return loading to avoid flash of content
    return (
      <div className="min-h-screen bg-gray-950 text-white p-6 flex items-center justify-center">
        <div className="fixed inset-0 -z-10 overflow-hidden">
          <OptimizedStarryBackground />
        </div>
        <div className="flex flex-col items-center gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            className="w-12 h-12 border-t-2 border-r-2 border-orange-300 rounded-full"
          />
          <p className="text-gray-300">Initierar vy...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-y-auto bg-gray-900 text-white relative" id="process-viewer-container">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10 overflow-hidden bg-gray-950" id="background-effects">
        <OptimizedStarryBackground />
      </div>
    
      {/* Header with the back button and business area tag properly placed */}
      <Header
        searchTerm={searchTerm}
        searchResults={searchResults}
        onSearchChange={handleSearch}
        onClearSearch={() => {
          setSearchTerm("");
          setSearchResults([]);
        }}
        onResultSelect={navigateToResult}
        processData={rawData}
        onFilterChange={handleFilterChange}
        onShowDocumentList={handleShowDocumentList}
        onStartTutorial={() => setIsTutorialOpen(true)}
        onShowQuiz={handleShowQuiz}
        businessArea={businessArea}
        category={category}
        onBackToAreas={onBackToAreas}
        onBackToCategories={onBackToCategories}
      />
  
      {/* Document list modal */}
      <DocumentListModal
        isOpen={isDocumentListOpen}
        onClose={() => setIsDocumentListOpen(false)}
        documents={documentList}
      />
  
      {/* Enhanced Tutorial guide */}
      <EnhancedTutorialGuide
        isOpen={isTutorialOpen}
        onClose={() => setIsTutorialOpen(false)}
        processData={rawData}
        onSelectMainProcess={setActiveMainProcess}
        onSelectSubProcess={setActiveSubProcess}
        onSelectMoment={setActiveMoment}
        activeMainProcess={activeMainProcess}
        activeSubProcess={activeSubProcess}
        activeMoment={activeMoment}
      />
      
      {/* Quiz modal */}
      <QuizModal
        isOpen={isQuizOpen}
        onClose={() => setIsQuizOpen(false)}
        processData={rawData}
      />
  
      {/* Process navigation with margin to compensate for fixed header and extra padding */}
      <div 
        className="bg-gray-800/10 backdrop-blur-md border-b border-gray-700/50" 
        id="process-navigation-container"
        style={{ marginTop: "80px" }} 
      >
        <div className="container mx-auto px-4 py-8">
          {/* Main process navigation */}
          <div id="main-process-navigation" className="mb-8">
            <ProcessNavigation
              processes={mainProcesses}
              activeProcess={activeMainProcess}
              onSelect={(title) => {
                setActiveMainProcess(title);
                const matchingItem = rawData.find((item) => item.Title === title);
                if (matchingItem) {
                  setActiveSubProcess(matchingItem.field_0);
                  setActiveMoment(matchingItem.field_2);
                }
              }}
              highlightedItems={highlightMap.mainProcesses}
              type="main"
            />
          </div>
          
          {/* Subprocess navigation */}
          <div id="sub-process-navigation">
            <ProcessNavigation
              processes={subProcesses}
              activeProcess={activeSubProcess}
              onSelect={(title) => {
                setActiveSubProcess(title);
                const firstMoment = rawData.find(
                  (item) =>
                    item.Title === activeMainProcess && item.field_0 === title
                )?.field_2;
                if (firstMoment) setActiveMoment(firstMoment);
              }}
              highlightedItems={
                highlightMap.subProcesses.get(activeMainProcess) || new Set()
              }
              type="sub"
            />
          </div>
        </div>
      </div>

      {/* Main content - Three-column layout with increased vertical padding and gap */}
      <main className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left column - Moment list with auto-height */}
          <div className="lg:col-span-3 flex flex-col moment-list" id="moment-list-column">
            <MomentList
              moments={moments}
              activeMoment={activeMoment}
              onSelectMoment={(title) => setActiveMoment(title)}
              highlightedItems={
                highlightMap.moments
                  .get(activeMainProcess)
                  ?.get(activeSubProcess) || new Set()
              }
            />
          </div>

          {/* Middle column - Moment details and participants */}
          <div className="lg:col-span-6" id="moment-details-column">
            <AnimatePresence mode="wait">
              {activeProcessData ? (
                <ImprovedMomentDetailLayout 
                  activeProcessData={activeProcessData}
                  originalProcessItem={originalProcessItem}
                  filteredRoles={filters.roles}
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-gray-400 py-8 bg-gray-800/20 border-gray-700/50 rounded-xl border p-6"
                  id="empty-details-placeholder"
                >
                  <p className="mb-2">Välj ett moment för att se detaljer</p>
                  <p className="text-xs text-gray-500">Momentdetaljer visas här när du väljer ett moment från listan</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Right column - Documents */}
          <div className="lg:col-span-3" id="documents-column">
            <ImprovedDocumentsColumn activeProcessData={activeProcessData} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProcessViewer;
